import { useState, useEffect, useRef } from 'react';
import { Check, ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

/**
 * AsyncCombobox - A generic autocomplete component with async search
 *
 * @param {string} value - The current selected value
 * @param {function} onValueChange - Callback when value changes (receives value string)
 * @param {function} onSelectionChange - Optional callback when selection changes (receives full data object)
 * @param {function} onSearch - Async function that returns search results
 *   Should return: Promise<Array<{ value: string, label: string, group?: string, data?: object }>>
 * @param {object} groupConfig - Optional configuration for groups
 *   Format: { [groupName]: { icon: LucideIcon, color: string } }
 * @param {function} onCreateNew - Optional callback when "Create new" is clicked
 * @param {string} createNewLabel - Label for create new option (default: "Create new...")
 * @param {string} createNewGroup - Group name where create button should appear (default: shows at bottom)
 * @param {string} placeholder - Placeholder text
 * @param {string} emptyText - Text to show when no results
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300)
 * @param {boolean} disabled - Whether the combobox is disabled
 * @param {string} className - Additional CSS classes
 * @param {string} displayLabel - Optional pre-known label for the selected value (avoids UUID flash)
 */
export function AsyncCombobox({
  value,
  onValueChange,
  onSelectionChange,
  onSearch,
  groupConfig = {},
  onCreateNew,
  createNewLabel = 'Create new...',
  createNewGroup,
  placeholder = 'Search...',
  emptyText = 'No results found.',
  debounceMs = 300,
  className,
  disabled = false,
  displayLabel
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimerRef = useRef(null);

  // Get display value - use displayLabel if provided, otherwise check results, fallback to placeholder
  const displayValue = displayLabel || results.find(item => item.value === value)?.label || placeholder;

  // Load initial results when popover opens
  useEffect(() => {
    if (open && results.length === 0 && !searchQuery && onSearch) {
      setIsLoading(true);
      onSearch('').then((initialResults) => {
        setResults(initialResults || []);
        setIsLoading(false);
      }).catch((error) => {
        console.error('Initial load error:', error);
        setIsLoading(false);
      });
    }
  }, [open]);

  // Debounce search input
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If search term is empty, clear results
    if (!searchQuery || searchQuery.trim() === '') {
      setResults([]);
      setIsLoading(false);
      return;
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(async () => {
      if (!onSearch) return;

      setIsLoading(true);
      try {
        const searchResults = await onSearch(searchQuery);
        setResults(searchResults || []);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, onSearch, debounceMs]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn('w-full justify-between', className)}
          disabled={disabled}
        >
          <span className='truncate'>{displayValue}</span>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-full p-0' align='start'>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {isLoading ? (
              <div className='flex items-center justify-center py-6'>
                <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                <span className='ml-2 text-sm text-muted-foreground'>
                  Searching...
                </span>
              </div>
            ) : (
              <>
                {/* Show empty state when no results and searching */}
                {results.length === 0 && searchQuery && !onCreateNew && (
                  <CommandEmpty>{emptyText}</CommandEmpty>
                )}

                {/* Show "Start typing" only when not searching and no create button */}
                {results.length === 0 && !searchQuery && !onCreateNew && (
                  <div className='py-6 text-center text-sm text-muted-foreground'>
                    Start typing to search...
                  </div>
                )}

                {/* Render results if we have them */}
                {results.length > 0 && (() => {
                  const hasGroups = results.some(r => r.group);

                  if (hasGroups) {
                    // Group results by the 'group' field
                    const grouped = results.reduce((acc, result) => {
                      const groupName = result.group || 'Other';
                      if (!acc[groupName]) {
                        acc[groupName] = [];
                      }
                      acc[groupName].push(result);
                      return acc;
                    }, {});

                    // If createNewGroup is specified and doesn't exist in results, add empty group
                    if (onCreateNew && createNewGroup && !grouped[createNewGroup]) {
                      grouped[createNewGroup] = [];
                    }

                    return Object.entries(grouped).map(([groupName, items]) => {
                      const config = groupConfig[groupName] || {};
                      const GroupIcon = config.icon;
                      const groupColor = config.color;
                      const shouldShowCreateButton = onCreateNew && createNewGroup === groupName;

                      return (
                        <CommandGroup
                          key={groupName}
                          heading={
                            <div className='flex items-center gap-1.5'>
                              {GroupIcon && (
                                <GroupIcon
                                  className='h-3.5 w-3.5'
                                  style={groupColor ? { color: groupColor } : undefined}
                                />
                              )}
                              <span style={groupColor ? { color: groupColor } : undefined}>
                                {groupName}
                              </span>
                            </div>
                          }
                        >
                          {items.map((result, idx) => (
                            <CommandItem
                              key={`${groupName}-${idx}-${result.value}`}
                              value={result.value}
                              onSelect={currentValue => {
                                const newValue = currentValue === value ? '' : currentValue;
                                onValueChange(newValue);

                                // Call onSelectionChange with full data if provided
                                if (onSelectionChange && newValue) {
                                  onSelectionChange(result.data || result);
                                }

                                setOpen(false);
                                setSearchQuery('');
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  value === result.value
                                    ? 'opacity-100'
                                    : 'opacity-0'
                                )}
                              />
                              {result.label}
                            </CommandItem>
                          ))}

                          {/* Create New Button - integrated into group */}
                          {shouldShowCreateButton && (
                            <CommandItem
                              value='__create_new_action__'
                              onSelect={() => {
                                setOpen(false);
                                setSearchQuery('');
                                onCreateNew(searchQuery);
                              }}
                              className='text-primary font-medium'
                            >
                              <Plus className='mr-2 h-4 w-4' />
                              {createNewLabel}
                            </CommandItem>
                          )}
                        </CommandGroup>
                      );
                    });
                  } else {
                    // No groups, render flat list
                    return (
                      <CommandGroup>
                        {results.map((result, idx) => (
                          <CommandItem
                            key={`${idx}-${result.value}`}
                            value={result.value}
                            onSelect={currentValue => {
                              const newValue = currentValue === value ? '' : currentValue;
                              onValueChange(newValue);

                              // Call onSelectionChange with full data if provided
                              if (onSelectionChange && newValue) {
                                onSelectionChange(result.data || result);
                              }

                              setOpen(false);
                              setSearchQuery('');
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                value === result.value
                                  ? 'opacity-100'
                                  : 'opacity-0'
                              )}
                            />
                            {result.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    );
                  }
                })()}

                {/* Always show create button group when configured, but ONLY when there are no results */}
                {/* If there are results, the button is already shown via the grouped logic above */}
                {onCreateNew && createNewGroup && results.length === 0 && (() => {
                  const config = groupConfig[createNewGroup] || {};
                  const GroupIcon = config.icon;
                  const groupColor = config.color;

                  return (
                    <CommandGroup
                      heading={
                        <div className='flex items-center gap-1.5'>
                          {GroupIcon && (
                            <GroupIcon
                              className='h-3.5 w-3.5'
                              style={groupColor ? { color: groupColor } : undefined}
                            />
                          )}
                          <span style={groupColor ? { color: groupColor } : undefined}>
                            {createNewGroup}
                          </span>
                        </div>
                      }
                    >
                      {results.length === 0 && searchQuery && (
                        <div className='px-2 py-1.5 text-sm text-muted-foreground'>
                          {emptyText}
                        </div>
                      )}
                      <CommandItem
                        value='__create_new_action__'
                        onSelect={() => {
                          setOpen(false);
                          setSearchQuery('');
                          onCreateNew(searchQuery);
                        }}
                        className='text-primary font-medium'
                      >
                        <Plus className='mr-2 h-4 w-4' />
                        {createNewLabel}
                      </CommandItem>
                    </CommandGroup>
                  );
                })()}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
